import { PlanChangePreview } from '@/services/subscription-plan-change';
import type { PlanChangeFailure } from '@/helpers/plan-change-error-copy';
import { getSwitchDirection } from '@/helpers/plan-switch';
import { isSinglePayment } from '@/helpers/subscription';
import type { SubscriptionCode } from '@/state/subscription-codes';
import {
  formatIntervalForTerms,
  formatPeriodLength,
  formatPrice,
} from '@/lib/utils';

// The plan a code's pending change moves it to, or null when nothing waits.
// A confirm whose response was lost cannot tell its own downgrade from one
// scheduled by somebody else, so the caller re-reads the code and asks this.
export const pendingChangeTarget = (
  codes: SubscriptionCode[],
  subscriptionCodeId: string
): string | null =>
  codes.find((c) => c.subscription_code_id === subscriptionCodeId)
    ?.pending_plan_change?.to_subscription.subscription_id ?? null;

export const buildUpgradeToastDescription = (
  preview: PlanChangePreview
): string | undefined => {
  const fromPerCycle = preview.current_plan.total_number_of_classes_per_cycle;
  const toPerCycle = preview.new_plan.total_number_of_classes_per_cycle;
  if (toPerCycle === null) return 'Unlimited credits unlocked.';
  const added =
    fromPerCycle != null && toPerCycle != null
      ? toPerCycle - fromPerCycle
      : null;
  if (added != null && added > 0) {
    return `${added} extra credit${added === 1 ? '' : 's'} added to your balance.`;
  }
  return undefined;
};

// Two presses per selected plan. The second is the only one that tells us
// anything: the same answer twice means the cause is stable.
export const MAX_CONFIRM_ATTEMPTS = 2;

export const canRetryPlanChange = (
  failure: PlanChangeFailure | null,
  failedConfirms: number
): boolean =>
  failure?.retryable === true && failedConfirms < MAX_CONFIRM_ATTEMPTS;

// A downgrade is quoted as null because nothing is charged today. An upgrade
// quoted as null is a proration preview the provider refused: confirming still
// charges the real difference, so no figure may be shown there.
export const isProrationUnknown = (
  isUpgrade: boolean,
  prorationAmount: number | null
): boolean => isUpgrade && prorationAmount == null;

export const buildSupportMailto = (email: string, reference: string): string =>
  `mailto:${email}?subject=${encodeURIComponent(`Plan change problem ${reference}`)}`;

// Every price that is not higher is a DOWNGRADE to the backend, so a switch to
// a plan at the same price is scheduled like a cheaper one. She reads it as a
// plan change, not a downgrade.
export const isSamePricePreview = (
  preview: Pick<PlanChangePreview, 'change_type' | 'current_plan' | 'new_plan'>
): boolean =>
  preview.change_type === 'DOWNGRADE' &&
  getSwitchDirection(preview.current_plan.price, preview.new_plan.price) ===
    'same';

// The code still names the plan she is leaving until the change applies.
export const isSamePricePendingChange = (
  code: Pick<SubscriptionCode, 'subscription' | 'pending_plan_change'>
): boolean =>
  !!code.pending_plan_change &&
  getSwitchDirection(
    code.subscription.price,
    code.pending_plan_change.to_subscription.price
  ) === 'same';

// The change-plan page's words once the preview is in, for every change that
// is not an upgrade: those wait for the period end.
export const scheduleChangeLabels = (
  preview: Pick<PlanChangePreview, 'change_type' | 'current_plan' | 'new_plan'>
): { button: string; scheduledToast: string } =>
  isSamePricePreview(preview)
    ? {
        button: 'Schedule plan change',
        scheduledToast: `Plan change to ${preview.new_plan.name} scheduled`,
      }
    : {
        button: 'Schedule downgrade',
        scheduledToast: `Downgrade to ${preview.new_plan.name} scheduled`,
      };

// The words for canceling a change that waits for the period end.
export const cancelPlanChangeLabels = (
  code: Pick<SubscriptionCode, 'subscription' | 'pending_plan_change'>
) =>
  isSamePricePendingChange(code)
    ? {
        button: 'Cancel plan change',
        canceledToast: 'Plan change canceled',
        failedFallback: 'Failed to cancel plan change',
        dialogTitle: 'Cancel your plan change?',
        keepLabel: 'Keep plan change',
        confirmLabel: 'Yes, cancel plan change',
      }
    : {
        button: 'Cancel downgrade',
        canceledToast: 'Downgrade canceled',
        failedFallback: 'Failed to cancel downgrade',
        dialogTitle: 'Cancel your downgrade?',
        keepLabel: 'Keep downgrade',
        confirmLabel: 'Yes, cancel downgrade',
      };

// No word for the change's direction. The banner's title already says a plan
// change, and a code whose waiting change did not load cannot say whether the
// new plan costs less.
export const pendingPlanChangeSentence = (date: string | null): string =>
  date
    ? `Your new plan starts on ${date}.`
    : 'Your plan will change at the end of the current billing cycle.';

type PendingTarget = NonNullable<
  SubscriptionCode['pending_plan_change']
>['to_subscription'];

// What the plan a scheduled change moves to will cost. A one-payment target is
// billed once when it starts, and the backend ends it after that period.
export const scheduledPlanPriceSentence = (
  newPlan: PendingTarget,
  currentPlan: Pick<SubscriptionCode['subscription'], 'price' | 'currency'>
): string => {
  if (isSinglePayment(newPlan)) {
    const term = formatPeriodLength(
      newPlan.charge_every_count || 1,
      newPlan.charge_every_interval
    );
    return newPlan.price
      ? `You'll then pay ${formatPrice(newPlan.price, newPlan.currency)} once for ${term}. It does not renew.`
      : `It's free for ${term} and does not renew.`;
  }
  if (!newPlan.price) return "It's free from then on.";
  return `You'll then be charged ${formatPrice(newPlan.price, newPlan.currency)} ${formatIntervalForTerms(newPlan.charge_every_count, newPlan.charge_every_interval)} instead of ${formatPrice(currentPlan.price, currentPlan.currency)}.`;
};
